/**
 * @sunday/choose: Help me choose (mockup v107, rules level B, 16 Oct 2026).
 * Six rows on one sheet (age, weight, sleep, feel, anything else, size) and one result: the pick at the
 * visitor's size, its EMI, the delivery day for a remembered pincode, the trial, two reason ticks, one real
 * 5-star review, "Send to family", one marigold button, Change answers and See a lower price.
 *
 * No words and no rules live here. Everything comes from the Sunday dock and sheets settings, printed by
 * snippets/sunday-pill.liquid as script[data-sd-choose-cfg]:
 *  - rules: one per line, "conditions > result | reason", applied in order (each match replaces the pick);
 *    conditions are age= wt= sl= fe= p= top= (comma = any of; != = none of), bp, !bp, hot, !hot;
 *    a result is a mattress code, "+t" adds the topper.
 *  - lower: the same form; the first match is the lower-price option.
 *  - concerns: "conditions > tick | reasons it repeats"; the first that applies and doesn't repeat the
 *    reason is the second tick.
 * Mattresses (names, colours, renders, prices by size, one review each) come from the records.
 *
 * Kept in this browser only (@sunday/state "choose": the answers). When the result shows: the size goes to
 * "bed" (it then follows the visitor to every page), the pick's handle to "mattress", and three cart
 * attributes are written once (the pick, the size, whether it's a pair; no age, weight or health answer).
 * Other parts read the pick with current() and listen for "sunday:pick".
 */
import * as state from '@sunday/state';
import { parse, cutFrom } from '@sunday/sizes';
import { money, esc, emi, fill } from '@sunday/util';
import { fmt, addDays } from '@sunday/delivery';

const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];

let cfg = null;
try {
  cfg = JSON.parse($('script[data-sd-choose-cfg]').textContent);
} catch (e) {
  cfg = null;
}
const T = (cfg && cfg.text) || {};

/* ---------- the rules (pure) ---------- */
function lines(src) {
  return String(src || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l && l.includes('>'))
    .map((l) => {
      const [lhs, rhs] = l.split('>');
      const [out, why] = rhs.split('|').map((x) => (x || '').trim());
      return { conds: lhs.trim().split(/\s+/).filter(Boolean), out, why };
    });
}
function test(tok, f) {
  if (tok === 'bp' || tok === 'hot') return !!f[tok];
  if (tok === '!bp' || tok === '!hot') return !f[tok.slice(1)];
  const m = /^(\w+)(!?=)(.+)$/.exec(tok);
  if (!m) return false;
  const v = String(f[m[1]] ?? '');
  const hit = m[3].split(',').includes(v);
  return m[2] === '=' ? hit : !hit;
}
const matches = (r, f) => r.conds.every((t) => test(t, f));
const outOf = (s) => ({ p: s.replace(/\+t$/i, ''), top: /\+t$/i.test(s) });

/** The pick for one set of answers. a = { age, wt, sl, fe }, ex = ['bp', 'hot']. */
export function decide(c, a, ex = []) {
  const kid = a.age === c.kid;
  const f = { age: a.age, wt: kid ? c.kidWeight : a.wt, sl: a.sl, fe: a.fe, bp: !kid && ex.includes('bp'), hot: !kid && ex.includes('hot'), p: '', top: '0' };
  let why = '';
  lines(c.rules).forEach((r) => {
    if (!matches(r, f)) return;
    const o = outOf(r.out);
    f.p = o.p;
    f.top = o.top ? '1' : '0';
    why = r.why;
  });
  const lowR = lines(c.lower).find((r) => matches(r, f));
  const alt = lowR ? { ...outOf(lowR.out), why: lowR.why } : null;
  const W = c.words || {};
  const t1 = W[why] || '';
  let t2 = '';
  for (const r of lines(c.concerns)) {
    if (!matches(r, f)) continue;
    const skip = (r.why || '').split(',').map((x) => x.trim());
    const w = W[r.out] || '';
    if (w && !skip.includes(why) && w !== t1) {
      t2 = w;
      break;
    }
  }
  return { p: f.p, top: f.top === '1', why, ticks: [t1, t2].filter(Boolean), alt: alt && { ...alt, ticks: [W[alt.why] || ''].filter(Boolean) } };
}

/* ---------- answers ---------- */
const saved = () => {
  const s = state.get('choose');
  return s && typeof s === 'object' ? { a: { ...(s.a || {}) }, ex: Array.isArray(s.ex) ? s.ex : [], seen: !!s.seen } : { a: {}, ex: [], seen: false };
};
const isKid = (a) => cfg && a.age === cfg.kid;
const need = (a) => ['age', 'sl', 'fe', 'sz'].concat(isKid(a) ? [] : ['wt']);
const done = (a) => need(a).every((k) => a[k]);

const mat = (code) => (cfg ? cfg.mattresses.find((m) => m.code === code) : null);
const sizeOf = (code) => (cfg ? cfg.sizes.find((s) => s.k === code) : null);

/** The answers' size: width from the answer; the visitor's own length when that width has it, else the setting. */
function bedSize(a) {
  const s = sizeOf(a.sz);
  if (!s) return null;
  const bed = state.get('bed');
  const l = bed && !bed.custom && Number(bed.w) === s.w && bed.l ? Number(bed.l) : cfg.len;
  return { w: s.w, l, word: s.label };
}
function priceAt(v, w, l) {
  const sizes = (v || []).map(([title, price]) => parse({ id: 0, title, price })).filter(Boolean);
  const at = sizes.filter((x) => x.w === w && x.l === l).sort((x, y) => x.price - y.price)[0] || cutFrom(sizes, w, l);
  return at ? at.price : 0;
}
function priced(o, sz) {
  const m = mat(o.p);
  if (!m) return null;
  const mp = priceAt(m.v, sz.w, sz.l);
  const tp = o.top && cfg.topper ? priceAt(cfg.topper.v, sz.w, sz.l) : 0;
  return { m, mp, tp, total: mp + tp, name: o.top ? fill(T.pair, { name: m.trim }) : m.name };
}

/** The visitor's pick, or null: { id, code, top, name, short, url, colour }. */
export function current() {
  if (!cfg) return null;
  const s = saved();
  if (!done(s.a)) return null;
  const d = decide(cfg, s.a, s.ex);
  const m = mat(d.p);
  return m ? { id: m.id, code: m.code, top: d.top, name: m.name, short: m.short, url: m.url, colour: m.colour } : null;
}

/* ---------- prices for the visitor's size on every mattress card ---------- */
function sizeWord(w) {
  const s = cfg && cfg.sizes.find((x) => x.w === w);
  return s ? s.label : '';
}
function paintPrices(root = document) {
  if (!cfg) return;
  const bed = state.get('bed');
  if (!bed || bed.custom || !bed.w || !bed.l) return;
  const word = sizeWord(Number(bed.w));
  if (!word) return;
  $$('[data-sd-mp]', root).forEach((el) => {
    const m = cfg.mattresses.find((x) => x.id === el.dataset.sdMp);
    const pr = m && priceAt(m.v, Number(bed.w), Number(bed.l));
    if (!pr) return;
    const p = $('[data-sd-mp-p]', el);
    const txt = fill(T.cardPrice, { price: money(pr), size: word });
    if (!p) return void (el.textContent = txt);
    $$('[data-sd-mp-w]', el).forEach((x) => (x.hidden = true));
    p.textContent = money(pr);
    let sz = $('[data-sd-mp-s]', el);
    if (!sz) {
      sz = document.createElement('span');
      sz.setAttribute('data-sd-mp-s', '');
      p.after(sz);
    }
    sz.textContent = fill(T.cardSize, { size: word });
    const e = $('[data-sd-mp-emi]', el);
    if (e && e.dataset.tpl) e.textContent = fill(e.dataset.tpl, { amount: money(emi(pr, Number(e.dataset.sdMpEmi) || 1)) });
  });
}

/* ---------- the sheet ---------- */
const root = cfg ? $('[data-sd-hc]') : null;

if (root && cfg) {
  const sheet = $('[data-hc-sheet]', root);
  const dim = $('[data-hc-dim]', root);
  const q = $('[data-hc-q]', root);
  const res = $('[data-hc-r]', root);
  const pill = $('[data-hc-pill]', root);
  const html = document.documentElement;
  let open = false;
  let scr = 'q';
  let low = false;
  let miss = false;
  let from = null;
  const wide = () => matchMedia('(min-width: 960px)').matches;

  const answers = () => saved();
  const save = (patch) => state.set('choose', { ...answers(), ...patch });

  /* the person the fit drawings already read (same answers, nothing new) */
  function person(a) {
    const P = cfg.person || {};
    const patch = { ...(P.wt && P.wt[isKid(a) ? cfg.kidWeight : a.wt]), touched: true };
    if (P.age && P.age[a.age]) patch.age = P.age[a.age];
    if (P.sl && P.sl[a.sl]) patch.pos = P.sl[a.sl];
    state.setPerson(patch);
  }

  function paintQ() {
    const { a, ex, seen } = answers();
    const kid = isKid(a);
    $$('[data-hc-row]', q).forEach((row) => {
      const k = row.dataset.hcRow;
      row.hidden = kid && (k === 'wt' || k === 'ex');
      row.classList.toggle('is-miss', miss && k !== 'ex' && !a[k] && !row.hidden);
      $$('[data-v]', row).forEach((b) => b.setAttribute('aria-pressed', String(k === 'ex' ? ex.includes(b.dataset.v) : a[k] === b.dataset.v)));
    });
    $('[data-hc-show]', q).hidden = !(seen && done(a));
  }

  function card(o, sz, best, ex) {
    const pr = priced(o, sz);
    if (!pr) return '';
    const m = pr.m;
    const img = Array.isArray(m.img) && m.img[0] ? `<img src="${esc(m.img[0])}" srcset="${esc(m.img[0])} 400w, ${esc(m.img[1] || m.img[0])} 800w" sizes="(min-width: 380px) 120px, 84px" width="${Number(m.img[2]) || 760}" height="${Number(m.img[3]) || 320}" alt="${esc(m.name)}" loading="lazy" decoding="async">` : '';
    const tp = o.top && cfg.topper && cfg.topper.img ? `<span class="sd-hc-tp"><img src="${esc(cfg.topper.img)}" alt="${esc(cfg.topper.name)}" width="64" height="64" loading="lazy" decoding="async"></span>` : '';
    let meta = '';
    if (best) {
      const months = Number(cfg.emiMonths) || 0;
      const pin = state.get('pincode');
      let arrive = T.free;
      if (pin && /^[1-9]\d{5}$/.test(pin)) {
        const z = (cfg.zones || []).find((x) => x.prefix && String(pin).startsWith(x.prefix)) || cfg.zoneDefault;
        if (z) arrive = `<b>${esc(fill(T.arrives, { date: fmt(addDays(new Date(), Number(z.max) || 0)) }))}</b>`;
        else arrive = esc(arrive);
      } else arrive = esc(arrive);
      meta = `${months ? `<p class="sd-hc-meta sunday-num">${esc(fill(T.emi, { amount: money(emi(pr.total, months)) }))}</p>` : ''}<p class="sd-hc-meta">${arrive}</p><p class="sd-hc-meta">${esc(T.nights)}</p>`;
    }
    const ticks = (o.ticks || []).map((t) => `<li><svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3.5 8.5l3 3 6-7"/></svg>${esc(t)}</li>`).join('');
    return `<article class="sd-hc-card${best ? ' is-best' : ''}" style="--sd-c:${esc(m.colour)};--sd-tint:${esc(m.tint || '')}">
      <div class="sd-hc-im">${img}${tp}</div>
      <div class="sd-hc-tx"><h3>${esc(pr.name)}</h3>
      <p class="sd-hc-pr"><b class="sunday-num">${money(pr.total)}</b> <span>${esc(sz.word)}</span></p>
      ${o.top ? `<p class="sd-hc-split sunday-num">${money(pr.mp)} + ${money(pr.tp)}</p>` : ''}
      ${meta}${ticks ? `<ul class="sd-hc-ticks">${ticks}</ul>` : ''}</div></article>`;
  }

  function review(code, ex) {
    const m = mat(code);
    const r = m && m.rev;
    if (!r) return '';
    const body = (ex.includes('bp') && r.bp) || (ex.includes('hot') && r.hot) || r.any;
    if (!body) return '';
    /* the mattress's rating and review count (Judge.me) on the stars line; else "Verified buyer" */
    const line = m.rating && m.count && T.revLine ? fill(T.revLine, { rating: m.rating, count: m.count }) : T.verified;
    return `<figure class="sd-hc-rev"><figcaption><span class="sd-hc-stars" role="img" aria-label="${esc(T.stars)}">★★★★★</span> ${esc(line)}</figcaption><blockquote>“${esc(body)}”</blockquote></figure>`;
  }

  let lastSig = '';
  function remember(d, sz, pr) {
    const m = pr.m;
    state.set('bed', { w: sz.w, l: sz.l, from: 'choose' });
    if (/^[a-z0-9-]+$/.test(m.id)) state.set('mattress', m.id);
    /* three cart attributes, once per result (no age, weight or health answers) */
    const A = cfg.attrs || {};
    const vals = { [A.pick]: m.name, [A.size]: `${sz.word} ${sz.l}×${sz.w}″`, [A.pair]: d.top ? A.yes : A.no };
    const sig = JSON.stringify(vals);
    if (sig === lastSig || sig === state.get('chosen')) return;
    lastSig = sig;
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    fetch(root + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes: vals }) })
      .then((r) => r.ok && state.set('chosen', sig))
      .catch(() => {});
  }

  function paintR() {
    const { a, ex } = answers();
    const d = decide(cfg, a, ex);
    const sz = bedSize(a);
    const pr = sz && priced(d, sz);
    if (!pr) return false;
    const m = pr.m;
    const msg = fill(T.shareText, { name: pr.name, size: sz.word, price: money(pr.total), url: m.abs || m.url });
    $('[data-hc-share]', res).href = 'https://wa.me/?text=' + encodeURIComponent(msg);
    const alt = d.alt && low ? card(d.alt, sz, false, ex) : '';
    const altM = d.alt && mat(d.alt.p);
    $('[data-hc-body]', res).innerHTML = `${card(d, sz, true, ex)}${review(d.p, ex)}
      <a class="sd-btn sd-full sd-hc-see" href="${esc(m.url)}#${esc(cfg.land)}" data-hc-go>${esc(d.top ? T.seePair : T.see)}</a>
      ${alt ? `<div class="sd-hc-alt">${alt}<a class="sd-hc-lnk" href="${esc(altM.url)}#${esc(cfg.land)}" data-hc-go>${esc(T.seeAlt)} ›</a></div>` : ''}
      <p class="sd-hc-links"><button type="button" class="sd-hc-lnk" data-hc-back>${esc(T.change)} ›</button>${d.alt && !low ? `<button type="button" class="sd-hc-lnk" data-hc-low>${esc(T.lower)} ›</button>` : ''}</p>`;
    remember(d, sz, pr);
    return true;
  }

  function paintPill() {
    const pk = current();
    const label = pk ? fill(T.yourPick, { name: pk.short }) : T.title;
    [pill, $('.sd-dock [data-sd-pill-open]')].forEach((b) => {
      if (!b) return;
      const l = $('[data-sd-dock-label], [data-hc-pill-label]', b);
      const dot = $('[data-hc-dot]', b);
      if (l) l.textContent = label;
      if (dot) {
        dot.hidden = !pk;
        if (pk) dot.style.background = pk.colour;
      }
      const ic = $('[data-hc-ic]', b);
      if (ic) ic.hidden = !!pk;
    });
  }

  function paint() {
    const { a } = answers();
    const showR = scr === 'r' && done(a);
    q.hidden = showR;
    res.hidden = !showR;
    if (showR && !paintR()) {
      q.hidden = false;
      res.hidden = true;
    }
    paintQ();
    paintPill();
    root.classList.toggle('is-open', open);
    oneMari();
    if (typeof place === 'function') place();
  }

  /* From 960 the panel sits over the page without a dim: its button turns navy while another marigold
     button is on screen (one marigold per screen). */
  function oneMari() {
    $$('.sd-hc-see, [data-hc-show]', sheet).forEach((b) => b.classList.remove('sd-nv'));
    if (!open || !wide()) return;
    $$('.sd-hc-see, [data-hc-show]', sheet).forEach((b) => {
      if (b.hidden || !b.offsetParent) return;
      const mari = getComputedStyle(b).backgroundColor;
      const other = $$('.sd-btn').some((x) => {
        if (root.contains(x) || !x.offsetParent) return false;
        const r = x.getBoundingClientRect();
        return r.bottom > 0 && r.top < innerHeight && r.width > 0 && getComputedStyle(x).backgroundColor === mari;
      });
      b.classList.toggle('sd-nv', other);
    });
  }

  let lastId;
  function announce() {
    const pk = current();
    const id = pk ? pk.id + (pk.top ? '+t' : '') : null;
    if (id === lastId) return;
    const first = lastId === undefined;
    lastId = id;
    moveTag(pk);
    if (!first || pk) document.dispatchEvent(new CustomEvent('sunday:pick', { detail: pk }));
  }

  function openSheet(trigger) {
    from = trigger || null;
    const { a, seen } = answers();
    scr = seen && done(a) ? 'r' : 'q';
    low = false;
    open = true;
    sheet.hidden = false;
    dim.hidden = wide();
    html.classList.add('sd-hc-open');
    if (!wide()) html.style.overflow = 'hidden';
    $$('[data-sd-pill-open], [data-hc-pill]').forEach((t) => t.setAttribute('aria-expanded', 'true'));
    paint();
    sheet.scrollTop = 0;
    const x = $('[data-hc-x]:not([hidden])', sheet.querySelector(':scope > :not([hidden])') || sheet);
    (x || sheet).focus({ preventScroll: true });
  }
  function closeSheet(back = true) {
    if (!open) return;
    open = false;
    sheet.hidden = true;
    dim.hidden = true;
    html.classList.remove('sd-hc-open');
    html.style.overflow = '';
    $$('[data-sd-pill-open], [data-hc-pill]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    paint();
    if (back && from && document.contains(from) && from.offsetParent !== null) from.focus({ preventScroll: true });
  }

  root.addEventListener('click', (e) => {
    const b = e.target.closest('button, a');
    if (!b) return;
    if (b.matches('[data-hc-x]')) return void closeSheet();
    if (b.matches('[data-hc-pill]')) return void (open ? closeSheet() : openSheet(b));
    if (b.matches('[data-hc-back]')) {
      scr = 'q';
      low = false;
      return void paint();
    }
    if (b.matches('[data-hc-low]')) {
      low = true;
      return void paint();
    }
    if (b.matches('[data-hc-show]')) {
      scr = 'r';
      paint();
      sheet.scrollTop = 0;
      return;
    }
    if (b.matches('[data-hc-go]')) return void closeSheet(false);
    const row = b.closest('[data-hc-row]');
    if (!row || !b.dataset.v) return;
    const k = row.dataset.hcRow;
    const v = b.dataset.v;
    const s = answers();
    if (k === 'ex') {
      save({ ex: s.ex.includes(v) ? s.ex.filter((x) => x !== v) : [...s.ex, v] });
    } else {
      const a = { ...s.a, [k]: v };
      const patch = { a };
      if (k === 'sz') {
        if (done(a)) {
          scr = 'r';
          miss = false;
          patch.seen = true;
        } else miss = true;
      } else if (!s.seen && done(a)) {
        /* first time through, in any order: the last missing answer shows the pick */
        scr = 'r';
        miss = false;
        patch.seen = true;
      }
      save(patch);
      if (a.age && a.sl && (a.wt || isKid(a))) person(a);
    }
    paint();
    if (scr === 'r') sheet.scrollTop = 0;
  });
  dim.addEventListener('click', () => closeSheet());
  document.addEventListener('keydown', (e) => {
    if (!open) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      closeSheet();
    } else if (e.key === 'Tab' && !wide()) {
      const f = $$('button:not([disabled]), a[href]', sheet).filter((x) => x.offsetParent !== null);
      if (!f.length) return;
      if (e.shiftKey && document.activeElement === f[0]) {
        e.preventDefault();
        f[f.length - 1].focus();
      } else if (!e.shiftKey && document.activeElement === f[f.length - 1]) {
        e.preventDefault();
        f[0].focus();
      }
    }
  });
  /* desktop: a tap outside the panel closes it */
  document.addEventListener('click', (e) => {
    if (!open || e.__sdHc || !wide()) return;
    if (e.target.closest('[data-hc-sheet], [data-hc-pill], [data-sd-pill-open]')) return;
    closeSheet(false);
  });

  /* every old entry point opens it: "#build" links, the dock's button, the old Build your bed address */
  document.addEventListener(
    'click',
    (e) => {
      const t = e.target.closest && e.target.closest('a[href="#build"], a[href*="build-your-bed"], [data-sd-pill-open]');
      if (!t || e.defaultPrevented) return;
      e.preventDefault();
      e.__sdHc = true;
      const menu = $('#ov');
      if (menu && !menu.hidden) {
        const x = $('[data-close]', menu);
        if (x) x.click();
      }
      if (open && t.matches('[data-sd-pill-open]')) closeSheet();
      else openSheet(t);
    },
    true
  );
  matchMedia('(min-width: 960px)').addEventListener('change', () => open && closeSheet(false));

  /* the desktop pill steps aside while it would sit on a button, field or price */
  const UNDER = 'a.sd-btn, button, input:not([type=hidden]), select, textarea, [data-add], [data-price], .sd-price';
  let raf = 0;
  function place() {
    if (!pill || !matchMedia('(min-width: 720px)').matches) return;
    root.classList.remove('is-under');
    const pr = pill.getBoundingClientRect();
    let hit = false;
    if (!open && pr.width) {
      hit = $$(UNDER).some((el) => {
        if (root.contains(el) || el.closest('.sd-dock, #ov, [hidden]')) return false;
        const r = el.getBoundingClientRect();
        return r.width > 0 && r.height > 0 && r.left < pr.right + 6 && r.right > pr.left - 6 && r.top < pr.bottom + 6 && r.bottom > pr.top - 6;
      });
    }
    root.classList.toggle('is-under', hit);
  }
  const again = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      place();
      if (open) oneMari();
    });
  };
  addEventListener('scroll', again, { passive: true });
  addEventListener('resize', again);
  const pw = $('.page-wrapper');
  if (pw) pw.addEventListener('scroll', again, { passive: true });

  document.addEventListener('sunday:state', (e) => {
    const k = e.detail && e.detail.key;
    if (k === 'choose') announce();
    if (k === 'bed') paintPrices();
  });
  paint();
  announce();
  place();
}

/* ---------- the Home tiles' tag follows the pick ---------- */
function moveTag(pk) {
  const tiles = $$('.sd-mtile[data-handle]');
  if (!tiles.length) return;
  const row = tiles[0].closest('[data-pick-tag]');
  const pickWord = (row && row.dataset.pickTag) || '';
  const defWord = (row && row.dataset.defaultTag) || '';
  tiles.forEach((t) => $$('.sd-mttag', t).forEach((x) => x.remove()));
  tiles.forEach((t) => {
    const hit = pk ? t.dataset.handle === pk.id : t.hasAttribute('data-default-tag');
    const word = pk ? pickWord : defWord;
    if (hit && word) {
      const s = document.createElement('span');
      s.className = 'sd-mttag';
      s.textContent = word;
      t.insertBefore(s, t.firstChild);
    }
  });
}

/* ---------- prices for the visitor's size, now, in the menu's template, and whenever the size changes ---------- */
if (cfg) {
  paintPrices();
  const tpl = $('template[data-menu-template]');
  if (tpl) paintPrices(tpl.content);
  if (!root) document.addEventListener('sunday:state', (e) => e.detail && e.detail.key === 'bed' && paintPrices());
}

/* ---------- from the result to a mattress page: land with the price and Add in view ---------- */
if (cfg && cfg.land && location.hash === '#' + cfg.land) {
  const land = () => {
    const add = $('#add');
    try {
      history.replaceState(null, '', location.pathname + location.search);
    } catch (e) {
      /* keep going */
    }
    if (!add) return;
    const pw = $('.page-wrapper');
    const inner = pw && pw.scrollHeight > pw.clientHeight + 1 && getComputedStyle(pw).overflowY !== 'visible';
    const r = add.getBoundingClientRect();
    const y = r.top - innerHeight * Number(cfg.landAt || 0.6);
    if (inner) pw.scrollTop += y;
    else scrollBy(0, y);
  };
  if (document.readyState === 'complete') setTimeout(land, 60);
  else addEventListener('load', () => setTimeout(land, 60), { once: true });
}
